// The call as the slip stands, priced by the SDK for both sides at the live mark: each side's bust,
// refusal, and what a move its way as far as its bust pays.

import { useEffect, useMemo } from "react";
import { pt, type Instrument, type TicketPreview } from "../../sdk";
import { big } from "../../lib/num";
import { useAccount, useOwner } from "../../state/account";
import { useMark } from "../../state/market";
import { useStore } from "../../state/store";
import { previews, slip } from "./slip-state";

/** The move a call's way as far as its bust, in basis points. */
function ladder(bustPpm: string | null | undefined): number[] {
  return [Math.max(1, Math.round(Number(big(bustPpm ?? "0") / 100n)))];
}

export type CallView = {
  up: TicketPreview;
  down: TicketPreview;
  /** The move priced, in basis points, in the call's favour. */
  moves: number[];
  useDebt: boolean;
  queued: bigint;
};

export function useCall(instrument: Instrument): CallView {
  const owner = useOwner();
  const account = useAccount();
  const mark = useMark(instrument.id);
  const state = useStore(slip);
  const queued = big(account?.funds.availableQueued);
  const useDebt = state.useDebt && queued > 0n;
  const markRaw = mark?.raw ?? null;

  const input = (isLong: boolean, movesBp: number[]) => ({
    owner,
    instrumentId: instrument.id,
    isLong,
    margin: state.stake,
    leverage: state.leverage,
    useDebt,
    mark: markRaw,
    movesBp,
  });

  // the previews read the account and trading state the SDK holds; these say when they moved
  const base = useMemo(
    () => pt.ticket(input(true, [])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [owner, instrument, state.stake, state.leverage, useDebt, markRaw, account],
  );
  const moves = ladder(base.bustPpm);
  const key = moves.join(",");
  const up = useMemo(
    () => pt.ticket(input(true, moves)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, key],
  );
  const down = useMemo(
    () => pt.ticket(input(false, moves.map((move) => -move))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, key],
  );

  useEffect(() => {
    previews.set({ instrumentId: instrument.id, up, down });
  }, [instrument.id, up, down]);
  useEffect(() => () => previews.set(null), []);

  return { up, down, moves, useDebt, queued };
}
